import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Anchor, Box, Button, Grommet, Page, ResponsiveContext } from "grommet";
import { normalizeColor } from "grommet/utils";
import * as React from "react";
import { SkeletonTheme } from "react-loading-skeleton";
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { usePermission, useUser } from "./account/AccountStore";
import ManagementHome from "./management/ManagementHome";
import { AnimatedGradient } from "./components/AnimatedGradient";
import DarkModeSwitch, { useDarkMode } from "./components/DarkModeSwitch";
import UserDiscordManagement from "./discord/UserDiscordManagement";
import { NotFound, useIFrame, usePath, useQuery } from "./general/Helpers";
import Home from "./Home";
import { MatchRowWrapper } from "./matches/MatchRow";
import { SeasonScheduleWrapper } from "./seasons/SeasonSchedule";
import { SeasonTableWrapper } from "./seasons/SeasonTable";
import { SeedMatchStatTableWrapper } from "./seeding/SeedMatchStatTable";
import { TeamLogoWrapper, TeamNameLogoWrapper, TeamNameWrapper } from "./teams/TeamName";
import {
    TournamentBracketWrapper,
    TournamentMatchRowWrapper,
    TournamentTeamLogoWrapper,
    TournamentTeamNameLogoWrapper,
    TournamentTeamNameWrapper,
    TournamentTeamPlayersWrapper,
    TournamentTeamPlayerWrapper,
    TournamentWinnerTeamLogoWrapper,
    TournamentWinnerTeamNameLogoWrapper,
    TournamentWinnerTeamNameWrapper,
    TournamentWinnerWrapper,
} from "./tournaments/LiveStreamComponents";
import { TournamentTeamPlayerGalaxyWrapper } from "./tournaments/themed/TournamentTeamPlayerGalaxyWrapper";

import UXAliasHome from "./userExperience/AliasHome";
import UXMatchHome from "./userExperience/MatchHome";
import UXTeamHome from "./userExperience/TeamHome";
import Theme from "./utils/Theme";
import TournamentHome from "./userExperience/TournamentHome";
import LeagueHome from "./userExperience/LeagueHome";
import SeasonHome from "./userExperience/SeasonHome";
import ErrorBoundary from "./general/ErrorBoundary";
import UXBadgeHome from "./userExperience/BadgeHome";
import { TournamentTeamPlayersGalaxyWrapper } from "./tournaments/themed/TournamentTeamPlayersGalaxyWrapper";
import UXParticipationBadgeHome from "./userExperience/ParticipationBadgeHome";
import { GetOrganizationInvites } from "./registration/RegistrationStore";
import { TournamentMatchPlayersGalaxyWrapper } from "./tournaments/themed/TournamentMatchPlayersGalaxyWrapper";
import ParallaxTilt from "./components/ParallaxTilt";
import ParallaxTilt2 from "./components/ParallaxTilt2";

const back = <FontAwesomeIcon icon={["fad", "arrow-left"]} size="lg"></FontAwesomeIcon>;
const homeIcon = <FontAwesomeIcon icon={["fad", "home"]} size="lg"></FontAwesomeIcon>;

const notificationIcon = (
    <span className="fa-layers fa-fw">
        <FontAwesomeIcon icon={["fad", "bell"]}></FontAwesomeIcon>
        <FontAwesomeIcon icon={["fad", "circle"]} transform={"shrink-10 up-4 right-4"} color="orange"></FontAwesomeIcon>
    </span>
);

const BrowserNavigation = () => {
    const navigate = useNavigate();
    let { key } = useLocation();

    const hasManagementPermission = usePermission("sport:management");
    const { pathname } = useLocation();

    const home = !(pathname.startsWith("/me") || pathname.startsWith("/public") || pathname.startsWith("/ue"));

    const size = React.useContext(ResponsiveContext);
    const isSmall = size === "small";

    return (
        <>
            {home && (
                <Box direction="row" pad={isSmall ? "medium" : "small"} gap={isSmall ? "medium" : "small"} align="center">
                    {key != "default" && <Button plain={!isSmall} onClick={() => navigate(-1)} icon={back} size={"medium"} />}
                    {key != "default" && <Button plain={!isSmall} onClick={() => navigate("/")} icon={homeIcon} size={"medium"} />}
                    {key == "default" && pathname != "/" && <Button plain={!isSmall} onClick={() => window.location.assign("/")} icon={homeIcon} size={"medium"} />}
                    {hasManagementPermission && (
                        <Button
                            plain={!isSmall}
                            href={"/me" + pathname}
                            label={
                                <Box direction="row" gap="xsmall" align="center">
                                    Management
                                </Box>
                            }
                            size={"medium"}
                        />
                    )}
                </Box>
            )}
        </>
    );
};

function App() {
    const [isDarkMode, toggleDarkMode] = useDarkMode();
    const [showDarkModeSwitch, setShowDarkModeSwitch] = React.useState(true);

    const inIFrame = useIFrame();
    const query = useQuery();
    const user = useUser();

    React.useEffect(() => {
        if (query.get("darkmode") || window.location.pathname.startsWith("/public")) {
            setShowDarkModeSwitch(false);
        }

        if (query.get("darkmode") == "true") {
            if (!isDarkMode) {
                toggleDarkMode && toggleDarkMode();
            }
        } else if (query.get("darkmode") == "false") {
            if (isDarkMode) {
                toggleDarkMode && toggleDarkMode();
            }
        }
    }, [inIFrame, toggleDarkMode]);

    return (
        <Grommet id="main" full theme={Theme} themeMode={isDarkMode ? "dark" : "light"}>
            <ErrorBoundary>
                <SkeletonTheme baseColor={normalizeColor("background-back", Theme, isDarkMode)} highlightColor={normalizeColor("background-front", Theme, isDarkMode)}>
                    {showDarkModeSwitch && <DarkModeSwitch></DarkModeSwitch>}
                    <div
                        style={{
                            position: "absolute",
                            right: "5rem",
                            top: "1rem",
                            zIndex: 1000,
                        }}
                        onClick={() => window.location.assign("/v2")}
                    >
                        <ParallaxTilt2 holo={true} sparkle={true}>
                            <div style={{ background: "#333333", padding: "0.75rem" }}>v2</div>
                        </ParallaxTilt2>
                    </div>
                    <BrowserRouter basename="public">
                        <Routes>
                            <Route
                                path={"/*"}
                                element={
                                    <Box style={{ position: "relative" }}>
                                        {/* <BrowserNavigation></BrowserNavigation> */}

                                        <Routes>
                                            <Route path="/tournaments/:id/" element={<TournamentBracketWrapper></TournamentBracketWrapper>}></Route>
                                            <Route path="/tournaments/:id/bracket" element={<TournamentBracketWrapper></TournamentBracketWrapper>}></Route>
                                            <Route path="/tournaments/:id/currentMatch" element={<TournamentMatchRowWrapper></TournamentMatchRowWrapper>}></Route>
                                            <Route path="/tournaments/:id/currentMatch/:participant/name" element={<TournamentTeamNameWrapper></TournamentTeamNameWrapper>}></Route>
                                            <Route path="/tournaments/:id/currentMatch/:participant/logo" element={<TournamentTeamLogoWrapper></TournamentTeamLogoWrapper>}></Route>
                                            <Route path="/tournaments/:id/currentMatch/:participant/nameWithLogo" element={<TournamentTeamNameLogoWrapper></TournamentTeamNameLogoWrapper>}></Route>
                                            <Route path="/tournaments/:id/currentMatch/:participant/players" element={<TournamentTeamPlayersWrapper></TournamentTeamPlayersWrapper>}></Route>
                                            <Route path="/tournaments/:id/currentMatch/players/galaxy" element={<TournamentMatchPlayersGalaxyWrapper></TournamentMatchPlayersGalaxyWrapper>}></Route>
                                            <Route
                                                path="/tournaments/:id/currentMatch/:participant/players/galaxy"
                                                element={<TournamentTeamPlayersGalaxyWrapper></TournamentTeamPlayersGalaxyWrapper>}
                                            ></Route>
                                            <Route path="/tournaments/:id/currentMatch/:participant/players/:player" element={<TournamentTeamPlayerWrapper></TournamentTeamPlayerWrapper>}></Route>
                                            <Route
                                                path="/tournaments/:id/currentMatch/:participant/players/:player/galaxy"
                                                element={<TournamentTeamPlayerGalaxyWrapper></TournamentTeamPlayerGalaxyWrapper>}
                                            ></Route>

                                            <Route path="/tournaments/:id/winner" element={<TournamentWinnerWrapper></TournamentWinnerWrapper>}></Route>
                                            <Route path="/tournaments/:id/winner/name" element={<TournamentWinnerTeamNameWrapper></TournamentWinnerTeamNameWrapper>}></Route>
                                            <Route path="/tournaments/:id/winner/logo" element={<TournamentWinnerTeamLogoWrapper></TournamentWinnerTeamLogoWrapper>}></Route>
                                            <Route path="/tournaments/:id/winner/nameWithLogo" element={<TournamentWinnerTeamNameLogoWrapper></TournamentWinnerTeamNameLogoWrapper>}></Route>
                                            <Route path="/matches/:id/" element={<UXMatchHome></UXMatchHome>}></Route>
                                            <Route path="/matches/:id/row" element={<MatchRowWrapper></MatchRowWrapper>}></Route>
                                            <Route
                                                path="/teams/:id/"
                                                element={
                                                    <Box fill align="center" justify="center">
                                                        <Box>
                                                            <UXTeamHome></UXTeamHome>
                                                        </Box>
                                                    </Box>
                                                }
                                            ></Route>
                                            <Route path="/teams/:id/name" element={<TeamNameWrapper></TeamNameWrapper>}></Route>
                                            <Route path="/teams/:id/logo" element={<TeamLogoWrapper></TeamLogoWrapper>}></Route>
                                            <Route path="/teams/:id/nameWithLogo" element={<TeamNameLogoWrapper></TeamNameLogoWrapper>}></Route>
                                            <Route path="/aliases/:id/" element={<NotFound></NotFound>}></Route>
                                            <Route path="/seedMatches/:id" element={<SeedMatchStatTableWrapper></SeedMatchStatTableWrapper>}></Route>
                                            <Route path="/seasons/:id/table" element={<SeasonTableWrapper></SeasonTableWrapper>}></Route>
                                            <Route path="/seasons/:id/schedule/*" element={<SeasonScheduleWrapper></SeasonScheduleWrapper>}></Route>
                                        </Routes>
                                    </Box>
                                }
                            ></Route>
                        </Routes>
                    </BrowserRouter>

                    <AnimatedGradient disabled={!user.isAuthenticated || window.location.pathname.startsWith("/public")}>
                        <BrowserRouter basename="">
                            <BrowserNavigation></BrowserNavigation>
                            <Routes>
                                <Route path="/" element={<Home />}></Route>
                                <Route path="/teams/:id/*" element={<UXTeamHome />}></Route>
                                <Route path="/aliases/:id/*" element={<UXAliasHome />}></Route>
                                <Route path="/matches/:id/*" element={<UXMatchHome></UXMatchHome>}></Route>
                                <Route path="/tournaments/:id/*" element={<TournamentHome></TournamentHome>}></Route>
                                <Route path="/leagues/:id/*" element={<LeagueHome></LeagueHome>}></Route>
                                <Route path="/seasons/:id/*" element={<SeasonHome></SeasonHome>}></Route>
                                <Route path="/discord" element={<UserDiscordManagement />}></Route>
                            </Routes>
                        </BrowserRouter>
                        <BrowserRouter basename="me">
                            <Routes>
                                <Route path={"/*"} element={<ManagementHome />}></Route>
                            </Routes>
                        </BrowserRouter>
                        <BrowserRouter basename="ue">
                            <BrowserNavigation></BrowserNavigation>
                            <Routes>
                                <Route
                                    path="/*"
                                    element={
                                        <Routes>
                                            <Route path="/teams/:id" element={<UXTeamHome />}></Route>
                                            <Route path="/aliases/:id" element={<UXAliasHome />}></Route>
                                            <Route path="/matches/:id/" element={<UXMatchHome></UXMatchHome>}></Route>
                                            <Route path="/badges/:id/" element={<UXBadgeHome></UXBadgeHome>}></Route>
                                            <Route path="/participationBadges/:id/" element={<UXParticipationBadgeHome></UXParticipationBadgeHome>}></Route>
                                        </Routes>
                                    }
                                ></Route>
                            </Routes>
                        </BrowserRouter>
                    </AnimatedGradient>
                </SkeletonTheme>
            </ErrorBoundary>
        </Grommet>
    );
}

export default App;
